import { FC, useCallback, useMemo, useState } from 'react';
import { Link } from 'ui/Link';
import { useForm } from 'react-final-form';
import { Popup } from 'components/Popup';
import { PayModal } from 'pages/about/PayModal';
import { Portal } from 'components/Portal/Portal';
import * as S from './ServiceCard.styled';
import { enablePageScroll } from 'utils/scroll';
import { moneyFormatter } from 'utils/intl';

export type ServiceCardProps = {
    no: number;
    title: string;
    description: string;
    price: number;
    disabled?: boolean;
    link?: string;
    onChoose?: (card: ServiceCardProps) => void;
    showNo?: boolean;
};

export const ServiceCard: FC<ServiceCardProps> = (props) => {
    const {
        no,
        title,
        description,
        price,
        link,
        onChoose,
        disabled,
        showNo = true
    } = props;

    const content = (
        <S.ServiceCardWrapper $disabled={disabled}>
            {showNo && <S.No>{no}</S.No>}
            <S.ServiceCardText>
                <S.ServiceCardTitle>{title}</S.ServiceCardTitle>
                <S.ServiceCardDescription>
                    {description}
                </S.ServiceCardDescription>
            </S.ServiceCardText>
            <S.ServiceCardPrice>
                {moneyFormatter.format(price)}
            </S.ServiceCardPrice>
        </S.ServiceCardWrapper>
    );

    return link ? (
        <Link to={link}>{content}</Link>
    ) : (
        <div onClick={() => onChoose?.(props)}>{content}</div>
    );
};

export const ServiceCardsCollection: FC<{
    cards: Omit<ServiceCardProps, 'no'>[];
    disabled?: boolean;
}> = ({ cards, disabled }) => {
    const form = useForm();
    const [isOpen, setIsOpen] = useState(false);

    const close = useCallback(() => {
        setIsOpen(false);
        enablePageScroll();
    }, []);

    const handleChoose = useCallback(
        (card: ServiceCardProps) => {
            if (disabled) {
                return;
            }

            setIsOpen(true);

            const quantity = Number(
                form.getState().values?.[`quantity`]?.[card.no] ?? 0
            );

            form.change(`quantity[${card.no}]`, quantity + 1);
        },
        [disabled, form]
    );

    const options = useMemo(() => {
        return cards.map((card, index) => ({
            ...card,
            no: index + 1
        }));
    }, [cards]);

    return (
        <>
            <S.Container>
                {options.map((card) => (
                    <ServiceCard
                        key={card.no}
                        {...card}
                        onChoose={handleChoose}
                        disabled={disabled}
                    />
                ))}
            </S.Container>
            <Portal>
                <Popup isOpen={isOpen} onClose={close}>
                    <PayModal options={options} />
                </Popup>
            </Portal>
        </>
    );
};
